import { toObjectified } from 'api/baseApi';
import { APIMethod, Payload, prepareUrl, useFetch, useFetchMutation, usePost, useUpdate, useDelete } from 'api/reactQuery/queryService';
import { Inline, objectify, objectifyAddress } from 'api/utils';
import { APIRoutes } from './constants';
import { ICreditNoteDetailsFacade, ICreditNoteFastLineFacade, ICreditNoteNotesFacade, IGetLineDetailsCriteria, IDeleteRecordResponse, IRestockingChargeCalculatorRequestFacade, IRestockingChargeFacade, IUpdateCreditNoteLineDetailsCriteriaFacade, IComponentLineDetailFacade, IComponentDetailFacade, IUpdateComponentLineDetailFacade } from 'api/swaggerTypes';
import { useQueryClient } from '@tanstack/react-query';
import { IBatchInfo } from 'components/common/DataGridDevEx/DataGrid.properties';
// Import types from Worksale for component details
import type { IObjectifiedComponentLineDetailResponse, IObjectifiedUpdateComponentLineDetailResponse } from 'api/Worksale/worksale';

const CreditNotesContextResponseParser = (response: any): any => {
  const { CreditNoteContext } = response;

  return {
    ...response,
    CreditNotesContext: toObjectified(CreditNoteContext as unknown as Inline<any>)
  };
};

export const useRetrieveCreditNotesContext = () =>
  useFetchMutation<Payload<{ WorkCreditNoteId: number }>, any>(
    APIRoutes.creditNotes.context.action,
    null,
    {
      inline: true,
      responseParser: CreditNotesContextResponseParser
    }
  );

export const useUpdateCreditNotesContext = () =>
  useUpdate<
    Payload<{
      WorkCreditNoteId: number;
      urlQueryParams: { ChangedField: string };
    } & Record<string, any>>,
    any
  >
    (APIRoutes.creditNotes.context.action, null, { inline: true, responseParser: CreditNotesContextResponseParser });

export const useApplyCreditNotesContext = () => {
  return usePost<Payload<{ WorkCreditNoteId: number } & Record<string, any>>, any>
    (APIRoutes.creditNotes.context.action, null, { inline: true, responseParser: CreditNotesContextResponseParser });
};

const invoiceLinesResponseParser = (response): any => {
  const { CreditInvoiceLines } = response;

  return {
    ...response,
    CreditInvoiceLines: CreditInvoiceLines?.map((o) => objectify(o as Inline<any>))
  };
};

export const useLoadInvoiceLines = () =>
  usePost<Payload<any>, any>(
    APIRoutes.creditNotes.invoiceLines.load,
    null,
    { responseParser: invoiceLinesResponseParser, inline: true }
  );

export const useFetchInvoiceLines = (WorkCreditNoteId: number) => {
  return useFetch<any>(
    {
      url: APIRoutes.creditNotes.invoiceLines.retrieve,
      urlParams: { WorkCreditNoteId },
      queryConfig: { enabled: !!WorkCreditNoteId },
      options: { inline: true, responseParser: invoiceLinesResponseParser }
    }
  );
};

export const useSetInvoiceLinesQueryData = () => {
  const queryClient = useQueryClient();

  return (WorkCreditNoteId: number, dataToUpdate: any) => {
    const finalUrl = prepareUrl(APIRoutes.creditNotes.invoiceLines.retrieve, { WorkCreditNoteId }, null, true);
    queryClient.setQueryData([APIMethod.GET, finalUrl], dataToUpdate);
  };
};

const creditNoteLinesResponseParser = (response): any => {
  const { CreditNoteGridLines } = response;

  return {
    ...response,
    CreditNoteGridLines: CreditNoteGridLines.map((o) => objectify(o as Inline<any>))
  };
};

export const useRetrieveCreditNoteLines = () =>
  useFetchMutation<any, any>
    (APIRoutes.creditNotes.lines.retrieve, null, { inline: true, responseParser: creditNoteLinesResponseParser });

const calculateFastLineResponseParser = (response) => (
  {
    ...response,
    CreditNoteFastLine: objectify(response?.CreditNoteFastLine),
    KitDetails: response?.KitDetails?.map((o) => objectify(o as Inline<any>)),
    InvoiceLines: response?.InvoiceLines?.map((o) => objectify(o as Inline<any>)),
  }
);

export const useCalculateFastLine = (validationCallbackFunc?: any) =>
  useUpdate<Payload<{ WorkCreditNoteId: number } & ICreditNoteFastLineFacade & { urlQueryParams: { ChangedField: string } }>, any>
    (APIRoutes.creditNotes.fastLine.calculate, null, { inline: true, validationCallBackFunc: validationCallbackFunc, responseParser: calculateFastLineResponseParser });

export const useCreateFastLine = () =>
  usePost<Payload<{ WorkCreditNoteId: number } & ICreditNoteFastLineFacade>, any>
    (APIRoutes.creditNotes.fastLine.create, null, { inline: false });

export const useDeleteCreditNoteLine = () => {

  return useDelete<Payload<{ WorkCreditNoteLineId: number }>, any>
    (APIRoutes.creditNotes.lines.delete, null, { inline: true });
};

export const useUpdateCreditNoteLine = () => {

  return usePost<Payload<{ WorkCreditNoteLineId: number; WorkCreditNoteId: number; CreditNoteGridLine: any }>, any>
    (APIRoutes.creditNotes.lines.update, null, { inline: true });
};

const creditNoteGridLineResponseParser = (response) => {

  return {
    ...response,
    CreditNoteGridLine: response.CreditNoteGridLine && toObjectified(response.CreditNoteGridLine as Inline<any>)
  };
};

export const useCalculateCreditNoteLine = () => {

  return useUpdate<Payload<{ WorkCreditNoteId: number; WorkCreditNoteLineId: number; CreditNoteGridLine: any; urlQueryParams: { ChangedField: string } } & any>, any>
    (APIRoutes.creditNotes.lines.calculate, null, { inline: true, responseParser: creditNoteGridLineResponseParser });
};

const calculateRestockingChargeResponseParser = (response) => {

  return {
    ...response,
    RestockingChargeCalculator: response.RestockingChargeCalculator && toObjectified(response.RestockingChargeCalculator as Inline<any>)
  };
};

export const useCalculateRestockingCharge = () => {

  return usePost<Payload<{ WorkCreditNoteId: number } & IRestockingChargeCalculatorRequestFacade>, any>
    (APIRoutes.creditNotes.restockingCalculator.calculate, null, { inline: true, responseParser: calculateRestockingChargeResponseParser });
};

const processResponseParser = (response) => {

  return {
    ...response,
    CreditNoteConfirmation: response?.CreditNoteConfirmation && toObjectified(response?.CreditNoteConfirmation)
  };
};

export const useProcess = () => {

  return usePost<Payload<{ WorkCreditNoteId: number } & any>, any>
    (APIRoutes.creditNotes.process, null, { inline: true, responseParser: processResponseParser });
};

const processConfirmationResponseParser = (response) => {

  return {
    ...response,
    CreditNoteConfirmation: toObjectified(response?.CreditNoteConfirmation)
  };
};

export const useProcessConfirmation = () => {

  return usePost<Payload<{ WorkCreditNoteId: number } & any>, any>
    (APIRoutes.creditNotes.processConfirmation, null, { inline: true, responseParser: processConfirmationResponseParser });
};

const restockingChargeDialogCalculateResponseParser = (response) => {

  return {
    ...response,
    RestockingCharge: response?.RestockingCharge && toObjectified(response.RestockingCharge as Inline<any>)
  };
};

export const useRestockingChargeDialogCalculate = () => {

  return usePost<Payload<{ WorkCreditNoteId: number } & IRestockingChargeFacade>, any>
    (APIRoutes.creditNotes.restockingCalculator.calculateRestockingCharge, null, { inline: true, responseParser: restockingChargeDialogCalculateResponseParser });
};

export const useCancelCreditNote = () => {

  return usePost<Payload<{ WorkCreditNoteId: number } & any>, any>
    (APIRoutes.creditNotes.cancel, null, { inline: false });
};

const notesResponseParser = (response): any => {

  return {
    ...response,
    Notes: response?.Notes && toObjectified(response.Notes as Inline<any>)
  };
};

export const useFetchCreditNoteNotes = (WorkCreditNoteId?: number) => {
  return useFetch<any>(
    {
      url: APIRoutes.creditNotes.notes,
      urlParams: { WorkCreditNoteId },
      queryConfig: { enabled: WorkCreditNoteId !== undefined && !!WorkCreditNoteId && WorkCreditNoteId !== 0 },
      options: { inline: true, responseParser: notesResponseParser }
    }
  );
};

export const useUpdateCreditNoteNotes = () => {

  return usePost<Payload<{ WorkCreditNoteId: number } & ICreditNoteNotesFacade>, any>
    (APIRoutes.creditNotes.notes, null, { inline: true, responseParser: notesResponseParser });
};

export const useDeleteCreditNotesLine = () => {

  return useDelete<Payload<{ WorkCreditNoteLineId: number }>, any>
    (APIRoutes.creditNotes.lineDelete, null, { inline: true });
};

// RETRIEVE LINE DETAILS

const creditNoteLineDetailsResponseParser = (response: any): any => {
  const { CreditNoteLineDetail } = response;

  return {
    ...response,
    CreditNoteLineDetail: CreditNoteLineDetail && toObjectified(CreditNoteLineDetail as unknown as Inline<any>)
  };
};

export const useFetchCreditNoteLineDetails = () => {
  return usePost<Payload<{ WorkCreditNoteLineId: number; urlQueryParams: { NotifyUnsavedChanges?: boolean } } & IGetLineDetailsCriteria>, any>
    (APIRoutes.creditNotes.lines.lineDetails.retrieve, null, { inline: true, responseParser: creditNoteLineDetailsResponseParser });
};
export const useFetchCancelLineDetails = () => {
  return usePost<Payload<{ WorkCreditNoteLineId: number } & IGetLineDetailsCriteria>, any>
    (APIRoutes.creditNotes.lines.lineDetails.cancel, null, { inline: true });
};
// UPDATE LINE DETAILS

const creditNoteLineDetailsUpdateResponseParser = (response: any): any => {
  const { CreditNoteLineDetail } = response;

  return {
    ...response,
    CreditNoteLineDetail: CreditNoteLineDetail && toObjectified(CreditNoteLineDetail as unknown as Inline<any>)
  };
};

export const useUpdateCreditNoteLineDetails = () => {
  return usePost<Payload<{ WorkCreditNoteLineId: number } & IUpdateCreditNoteLineDetailsCriteriaFacade>, any>
    (APIRoutes.creditNotes.lines.lineDetails.update, null, { inline: true, responseParser: creditNoteLineDetailsUpdateResponseParser });
};

// CALCULATE LINE DETAILS

const creditNoteLineDetailsCalculateResponseParser = (response: any): any => {
  const { CreditNoteLineDetail } = response;

  return {
    ...response,
    CreditNoteLineDetail: CreditNoteLineDetail && toObjectified(CreditNoteLineDetail as unknown as Inline<any>)
  };
};

export const useCalculateCreditNoteLineDetails = () => {
  return useUpdate<Payload<{ WorkCreditNoteLineId: number; urlQueryParams: { ChangedField: string } } & IUpdateCreditNoteLineDetailsCriteriaFacade>, any>
    (APIRoutes.creditNotes.lines.lineDetails.calculate, null, { inline: true, responseParser: creditNoteLineDetailsCalculateResponseParser });
};

const creditNotesSummaryResponseParser = (response) => {
  const { CreditNoteSummary } = response;

  return {
    ...response,
    CreditNoteSummary: toObjectified(CreditNoteSummary)
  };
};

export const useRetrieveCreditNotesSummary = (WorkCreditNoteId: number) =>
  useFetch<any>(
    {
      url: APIRoutes.creditNotes.summary.retrieve,
      urlParams: { WorkCreditNoteId },
      options: { inline: true, responseParser: creditNotesSummaryResponseParser },
      queryConfig: { enabled: !!WorkCreditNoteId }
    }
  );

const creditNoteDetailsResponseParser = (response) => {
  const CreditNoteDetails = response?.CreditNoteDetails && toObjectified(response.CreditNoteDetails as Inline<any>);

  return {
    ...response,
    CreditNoteDetails: {
      inlineObject: {
        ...CreditNoteDetails?.inlineObject,
        DeliveryAddress: objectifyAddress(CreditNoteDetails?.inlineObject, 'Delivery', ['Name', 'Address1', 'Address2', 'City', 'State', 'PostCode', 'Country']),
        PostalAddress: objectifyAddress(CreditNoteDetails?.inlineObject, 'Postal', ['Name', 'Address1', 'Address2', 'City', 'State', 'PostCode', 'Country'])
      },
      schema: CreditNoteDetails?.schema
    }
  };
};

export const useFetchCreditNoteDetails = (WorkCreditNoteId?: number) => {
  return useFetch<any>(
    {
      url: APIRoutes.creditNotes.details,
      urlParams: { WorkCreditNoteId },
      queryConfig: { enabled: WorkCreditNoteId !== undefined && !!WorkCreditNoteId && WorkCreditNoteId !== 0 },
      options: { inline: true, responseParser: creditNoteDetailsResponseParser }
    }
  );
};

export const useSetCreditNoteDetailsQueryData = () => {
  const queryClient = useQueryClient();

  return (WorkCreditNoteId: number, dataToUpdate: any) => {
    const finalUrl = prepareUrl(APIRoutes.creditNotes.details, { WorkCreditNoteId }, null, true);
    queryClient.setQueryData([APIMethod.GET, finalUrl], dataToUpdate);
  };
};

export const useCalculateCreditNoteDetails = () => {

  return useUpdate<Payload<{ WorkCreditNoteId: number; urlQueryParams: { ChangedField: string } } & ICreditNoteDetailsFacade>, any>
    (APIRoutes.creditNotes.details, null, { inline: true, responseParser: creditNoteDetailsResponseParser });
};

export const useUpdateCreditNoteDetails = () => {

  return usePost<Payload<{ WorkCreditNoteId: number } & ICreditNoteDetailsFacade>, any>
    (APIRoutes.creditNotes.details, null, { inline: true, responseParser: creditNoteDetailsResponseParser });
};

const parkCreditNoteResponseParser = (response): any => {

  return {
    ...response
  };
};

export const useParkCreditNotes = () => {

  return usePost<Payload<{ WorkCreditNoteId: number } & any>, any>
    (APIRoutes.creditNotes.park, null, { inline: true, responseParser: parkCreditNoteResponseParser });
};

const retrieveParkedCreditNotesResponseParser = (response) => {

  return {
    ...response,
    ParkedCreditNotes: response.ParkedCreditNotes ? response.ParkedCreditNotes.map((d) => objectify({ ...d, DateTime: `${d?.EnteredDate.Value} ${d?.EnteredTime?.Value}` }, null, 'Inline')) : []
  };
};

export const useRetrieveParkedCreditNotes = () => {

  return usePost<Payload<{ Entity?: string; Username?: string; CustomerId?: number | string; ParkedCreditNotesOnly?: boolean }, IBatchInfo>, any>
    (APIRoutes.creditNotes.recall, null, { inline: true, responseParser: retrieveParkedCreditNotesResponseParser });
};

const retrieveComponentLinesResponseParser = (response) => {

  return {
    ...response,
    Components: response.Components ? response.Components.map((d) => objectify({ ...d }, null, 'Inline')) : []
  };
};

export const useRetrieveComponentLines = () => {

  return usePost<Payload<{ WorkCreditNoteLineId: number }, any>, any>
    (APIRoutes.creditNotes.kitComponents.line.retrieve, null, { inline: true, responseParser: retrieveComponentLinesResponseParser });
};

const totalComponentLinesCountResponseParser = (response): Promise<number> => {

  return response?.BatchInformation?.TotalCount;
};

export const useTotalComponentLinesCount = () => {

  return usePost<Payload<{ WorkCreditNoteLineId: number }, any>, any>
    (APIRoutes.creditNotes.kitComponents.line.retrieve, null, { inline: true, responseParser: totalComponentLinesCountResponseParser });
};

const getCreditNoteLineSummaryResponseParser = (response) => {

  return {
    ...response,
    kitSummary: response?.Summary && toObjectified(response.Summary as Inline<any>)
  };
};

export const useFetchInvoiceLinesLookupPanel = () =>
  useFetchMutation<Payload<{ WorkCreditNoteId: number }>, any>(
    APIRoutes.creditNotes.invoiceLines.retrieve,
    null,
    {
      inline: true,
      responseParser: invoiceLinesResponseParser
    }
  );

export const useGetCreditNoteLineSummary = () => {

  return useFetchMutation<Payload<{ WorkCreditNoteLineId: number }>, any>(
    APIRoutes.creditNotes.kitComponents.summary,
    null,
    {
      inline: true,
      responseParser: getCreditNoteLineSummaryResponseParser
    }
  );
};

export const useDeleteComponentLine = () =>
  useDelete<Payload<{ ComponentId: number }>, IDeleteRecordResponse>(
    APIRoutes.creditNotes.kitComponents.line.delete
  );

const addComponenetResponseParser = (response) => ({
  ...response,
  ComponentLine: response.ComponentLine && toObjectified(response.ComponentLine as Inline<any>)
});

export const useUpdateComponentGridLineDetails = () =>
  usePost<Payload<any & { ComponentId: number }>, any>
    (APIRoutes.creditNotes.kitComponents.line.update, null, { inline: true, responseParser: addComponenetResponseParser });

const componentFastlineDetailsResponseParser = (response): Promise<any> => {
  const { ComponentDetails } = response;

  return {
    ...response,
    ComponentDetails: ComponentDetails ? toObjectified(ComponentDetails as Inline<any>) : null
  };
};

const calculateFastlineResponseParser = (response): Promise<any> => {
  const { ComponentDetails } = response;

  return {
    ...response,
    ComponentDetails: ComponentDetails ? toObjectified(ComponentDetails as Inline<any>) : null
  };
};

export const useCalculateComponentGridLineDetails = () =>
  useUpdate<Payload<any & { ComponentId: number }, { ChangedField: string }>, any>
    (APIRoutes.creditNotes.kitComponents.line.calculate, null, { inline: true, responseParser: componentFastlineDetailsResponseParser });

export const useCalculateComponentFastLineDetails = () => {

  return useUpdate<Payload<{ WorkCreditNoteLineId: number; ProductId?: number; RequiredQuantity?: number }>, any>(
    APIRoutes.creditNotes.kitComponents.fastline.calculate,
    null,
    {
      inline: true,
      responseParser: calculateFastlineResponseParser
    }
  );
};

export const useCreateComponentLine = () => {

  return usePost<Payload<{ WorkCreditNoteLineId: number; ProductId: number; RequiredQuantity: number; urlQueryParams?: { ProductId: string | number; RequiredQuantity: string | number } }>, any>
    (APIRoutes.creditNotes.kitComponents.fastline.create, null, { inline: true, responseParser: addComponenetResponseParser });
};

export const useLoadKitComponents = () =>
  usePost<Payload<any & { ComponentId: number }>, any>
    (APIRoutes.creditNotes.kitComponents.load, null, { inline: true, responseParser: retrieveComponentLinesResponseParser });

// COMPONENT DETAILS

const fetchComponentLineDetailResponseParser = (response): Promise<IObjectifiedComponentLineDetailResponse> => {
  const { ComponentLineDetail = {} } = response;

  return {
    ...response,
    ComponentLineDetail: ComponentLineDetail ? toObjectified(ComponentLineDetail as Inline<IComponentLineDetailFacade>) : null
  };
};

export const useFetchComponentLineDetail = () => {
  return useFetchMutation<Payload<{ ComponentId: number }>, IObjectifiedComponentLineDetailResponse>(
    APIRoutes.creditNotes.kitComponents.details.retrieve,
    null,
    {
      inline: true,
      responseParser: fetchComponentLineDetailResponseParser
    }
  );
};

const calculateComponentLineDetailResponseParser = (response): Promise<IObjectifiedComponentLineDetailResponse> => {
  const { Components, ComponentLineDetail } = response;

  // Handle new response structure where data is in Components
  const componentData = Components || ComponentLineDetail;

  if (!componentData) {
    return {
      ...response,
      ComponentLineDetail: null
    };
  }

  // Objectify the component data - toObjectified will extract Value from each property
  const objectified = toObjectified(componentData as Inline<IComponentLineDetailFacade>);

  return {
    ...response,
    ComponentLineDetail: objectified
  };
};

export const useCalculateComponentLineDetail = () => {
  return useUpdate<
    Payload<IComponentDetailFacade & { WorkCreditNoteLineId: number; ComponentId: number; urlQueryParams: { ChangedField: string } }>,
    IObjectifiedComponentLineDetailResponse
  >(
    APIRoutes.creditNotes.kitComponents.details.calculate.componentLineDetail,
    null,
    { inline: true, responseParser: calculateComponentLineDetailResponseParser }
  );
};

const updateComponentDetailResponseParser = (response): Promise<IObjectifiedUpdateComponentLineDetailResponse> => {
  const { ComponentLineDetail } = response;

  return {
    ...response,
    ComponentLineDetail: ComponentLineDetail ? toObjectified(ComponentLineDetail as Inline<IUpdateComponentLineDetailFacade>) : null
  };
};

export const useUpdateComponentDetail = () => {
  return usePost<Payload<IUpdateComponentLineDetailFacade & { WorkCreditNoteLineId: number; ComponentId: number }>, IObjectifiedUpdateComponentLineDetailResponse>
    (APIRoutes.creditNotes.kitComponents.details.update, null, { inline: true, responseParser: updateComponentDetailResponseParser });
};
